// src/components/about/ProfessionalHero.tsx
import { useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { siteConfig } from '../../config/siteConfig';
import { aboutConfig } from '../../config/aboutConfig';
import Button from '../Button';
import { motionSet, STAGGER } from './motion';
import {
  HeroRoot,
  HeroContainer,
  HeroMain,
  TitleBlock,
  Eyebrow,
  EyebrowMark,
  Name,
  Role,
  Supporting,
  ExperienceNote,
  ExperienceValue,
  ExperienceLabel,
  ClosingBlock,
  Principle,
  CtaRow,
  PrimaryCta,
  FocusPanel,
  PanelLabel,
  FocusList,
  FocusItem,
  FocusLabel,
  FocusMarker,
  FocusDetail,
} from './ProfessionalHero.styles';

const headingId = 'about-hero-name';

const ProfessionalHero = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion() ?? false;

  const { profile } = aboutConfig;
  const motion = motionSet(reduceMotion);
  const rise = motion.reveal;

  return (
    <HeroRoot aria-labelledby={headingId}>
      <HeroContainer>
        <HeroMain variants={motion.group(STAGGER.group, 0.04)} initial="hidden" animate="visible">
          <TitleBlock variants={rise}>
            <Eyebrow>
              <EyebrowMark aria-hidden="true" />
              {profile.eyebrow}
            </Eyebrow>
            <Name id={headingId}>{siteConfig.name}</Name>
            <Role>{profile.role}</Role>
          </TitleBlock>

          <Supporting variants={rise}>{profile.supporting}</Supporting>

          <ExperienceNote variants={rise}>
            <ExperienceValue>{profile.experience.value}</ExperienceValue>
            <ExperienceLabel>{profile.experience.label}</ExperienceLabel>
          </ExperienceNote>

          <ClosingBlock variants={rise}>
            <Principle>{profile.principle}</Principle>
            <CtaRow>
              <PrimaryCta
                label={profile.cta.primary.label}
                onClick={() => navigate(profile.cta.primary.to)}
              />
              <Button
                label={profile.cta.secondary.label}
                onClick={() => navigate(profile.cta.secondary.to)}
              />
            </CtaRow>
          </ClosingBlock>
        </HeroMain>

        <FocusPanel
          variants={rise}
          initial="hidden"
          animate="visible"
          aria-label="Engineering focus areas"
        >
          <PanelLabel>Focus areas</PanelLabel>
          <FocusList>
            {profile.focusAreas.map((area) => (
              <FocusItem key={area.label}>
                <FocusLabel>
                  <FocusMarker aria-hidden="true">▸</FocusMarker>
                  {area.label}
                </FocusLabel>
                <FocusDetail>{area.detail}</FocusDetail>
              </FocusItem>
            ))}
          </FocusList>
        </FocusPanel>
      </HeroContainer>
    </HeroRoot>
  );
};

export default ProfessionalHero;
